import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useRef,
	useLayoutEffect,
} from "react";
import styled from "styled-components";
import gsap from "gsap";

const ModalProviderWrapper = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	z-index: 100002;
	flex-grow: initial;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
`;

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.7);
	backdrop-filter: blur(0px);
	z-index: 100001;
	opacity: 0;
`;

type ModalContextType = {
	openModal: (component: ReactNode) => void;
	closeModal: () => Promise<void>;
	isOpen: boolean;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error("useModal must be used within a ModalProvider");
	}
	return context;
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [modalComponent, setModalComponent] = useState<ReactNode | null>(null);
	const modalRef = useRef<HTMLDivElement>(null);
	const overlayRef = useRef<HTMLDivElement>(null);
	const [isClosing, setIsClosing] = useState(false);

	// Animate in when modal opens
	useLayoutEffect(() => {
		if (isOpen && !isClosing && modalRef.current && overlayRef.current) {
			// Animate overlay: fade in and blur
			gsap.to(overlayRef.current, {
				opacity: 1,
				backdropFilter: "blur(30px)",
				duration: 0.325,
				ease: "power2.out"
			});

			// Animate wrapper: fade in
			gsap.to(modalRef.current, {
				opacity: 1,
				duration: 0.325,
				ease: "power2.out"
			});
		}
	}, [isOpen, isClosing]);

	const openModal = (component: ReactNode) => {
		setModalComponent(component);
		setIsOpen(true);
		setIsClosing(false);
	};

	const closeModal = async () => {
		if (modalRef.current && overlayRef.current && !isClosing) {
			setIsClosing(true);

			// Animate out overlay: fade out and remove blur
			const overlayAnimation = gsap.to(overlayRef.current, {
				opacity: 0,
				backdropFilter: "blur(0px)",
				duration: 0.325,
				ease: "power2.in"
			});

			// Animate out wrapper: fade out
			const wrapperAnimation = gsap.to(modalRef.current, {
				opacity: 0,
				duration: 0.325,
				ease: "power2.in"
			});

			// Wait for both animations to complete
			await Promise.all([overlayAnimation, wrapperAnimation]);

			setIsOpen(false);
			setModalComponent(null);
			setIsClosing(false);
		}
	};

	return (
		<ModalContext.Provider value={{ openModal, closeModal, isOpen }}>
			{children}
			{isOpen && (
				<>
					<ModalOverlay ref={overlayRef} />
					<ModalProviderWrapper ref={modalRef}>
						{modalComponent}
					</ModalProviderWrapper>
				</>
			)}
		</ModalContext.Provider>
	);
};
